import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { fetchItemDetail, type ItemDetailResponse } from "../api";
import { C } from "../tokens";

const ACCEPTED = "ledai:review-accepted";
interface AcceptedItem { itemId: string; search: string; startedAt: number }

export function notifyReviewAccepted(item: AcceptedItem) {
  window.dispatchEvent(new CustomEvent(ACCEPTED, { detail: item }));
}

export function syncedReviewPlatform(sync: ItemDetailResponse["integrationSync"], startedAt: number): string | null {
  for (const [key, label] of [["procore", "Procore"], ["autodesk", "Autodesk"]] as const) {
    const state = sync?.[key];
    if (state?.status === "synced" && state.syncedAt && Date.parse(state.syncedAt) >= startedAt) return label;
  }
  return null;
}

/** Lives outside review cards so accepting the last card cannot dismiss it. */
export function ReviewSyncToast() {
  const [message, setMessage] = useState("");
  useEffect(() => {
    let disposed = false;
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const pending = new Set<string>();
    let dismiss: ReturnType<typeof setTimeout> | undefined;
    const later = (fn: () => void, ms: number) => {
      const timer = setTimeout(() => { timers.delete(timer); fn(); }, ms);
      timers.add(timer);
      return timer;
    };
    const accepted = (event: Event) => {
      const item = (event as CustomEvent<AcceptedItem>).detail;
      if (pending.has(item.itemId)) return;
      pending.add(item.itemId);
      const poll = async (attempt: number) => {
        if (disposed) return;
        try {
          const detail = await fetchItemDetail(item.itemId, item.search);
          if (disposed) return;
          const platform = syncedReviewPlatform(detail.integrationSync, item.startedAt);
          if (platform) {
            pending.delete(item.itemId);
            setMessage(`Successfully synced to ${platform}`);
            if (dismiss) { clearTimeout(dismiss); timers.delete(dismiss); }
            dismiss = later(() => setMessage(""), 2500);
            return;
          }
        } catch {
          // A failed status read must never turn into a success notification.
        }
        if (!disposed && attempt < 29) later(() => void poll(attempt + 1), 2000);
        else pending.delete(item.itemId);
      };
      void poll(0);
    };
    window.addEventListener(ACCEPTED, accepted);
    return () => {
      disposed = true;
      window.removeEventListener(ACCEPTED, accepted);
      timers.forEach(clearTimeout);
    };
  }, []);
  return createPortal(
    <div role="status" aria-live="polite" aria-atomic="true" style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 100, pointerEvents: "none", maxWidth: "calc(100vw - 32px)" }}>
      {message && <div data-testid="review-sync-toast" style={{ padding: "10px 16px", borderRadius: 12, background: C.surface, color: C.ink, border: `1px solid ${C.line2}`, boxShadow: "0 4px 16px rgba(0,0,0,0.08)", fontSize: 14.95 }}>{message}</div>}
    </div>, document.body,
  );
}
